import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ArrowDown, Trash2, Lock } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import TextInput from '../../components/ui/TextInput.jsx';
import CheckboxInput from '../../components/ui/CheckboxInput';
import GenericSelect from '../../components/ui/GenericSelect';
import Button from '../../components/ui/Button';
import { useRatingScalesApi } from './config';

// Skorlama Plan 2, Task 10 — Puan Skalaları sekmesi. Liste+drawer iskeleti
// CompetenciesTab'ın deseni (DataTable + OptionsMenu + ConfirmDialog), ama
// GenericFormDrawer'ın şema-tabanlı alanları İKİ şeyi TAŞIYAMIYOR: (1)
// değişken sayıda (2-10) seviye satırı + ekle/sil/sırala, (2) `disabled` —
// switch-case'teki text/textarea dalları bunu hiç okumuyor (yalnız
// asyncSelect'te var), o yüzden kilitli skalada "ad" alanını salt-okunur
// yapamazdık. Çözüm: GenericFormDrawer yalnız KABUK olarak kullanılıyor
// (başlık/kapat/Kaydet-İptal) — `schema=[]`, ad + seviyeler `children`
// içinde kendi state'imizle yönetiliyor (PerformanceSettingsPage'in
// likertLabels'ı `form`'dan AYRI taslak tutma deseninin aynısı). Kilitliyken
// bu state'e `disabled=true` geçilir; tek mutasyon "Kopyala" butonudur.
const Hint = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 12px;
`;

const LevelsSection = styled.div`
  margin-top: 4px;
`;

const LevelsSectionTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 4px;
`;

const LevelCountHint = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 12px;
`;

const LevelCard = styled.div`
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  padding: 14px;
  margin-bottom: 10px;
  background: #fafbfc;
`;

const LevelCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const LevelIndex = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const LevelControls = styled.div`
  display: flex;
  gap: 4px;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  color: #667;
  &:hover:not(:disabled) { border-color: #0291EF; color: #0291EF; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const RemoveIconBtn = styled(IconBtn)`
  &:hover:not(:disabled) { border-color: #E53935; color: #E53935; }
`;

const LevelFieldsRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 10px;
  margin-bottom: 10px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SaveErrorText = styled.p`
  font-size: 12px;
  color: #B02A37;
  margin: 8px 0;
  font-weight: 600;
`;

const LockedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  background: rgba(229, 57, 53, 0.08);
  color: #E53935;
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 14px;
`;

const LockedHint = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin: 10px 0;
`;

const MIN_LEVELS = 2;
const MAX_LEVELS = 10;

function levelsFromRow(row) {
  return (row?.levels || [])
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((l) => ({ _key: l.id, id: l.id, label: l.label, numericValue: l.numericValue, triggersPip: !!l.triggersPip, band: l.band || null }));
}

function emptyLevels() {
  return [
    { _key: 'new-1', label: '', numericValue: 1, triggersPip: false, band: null },
    { _key: 'new-2', label: '', numericValue: 2, triggersPip: false, band: null },
  ];
}

export default function RatingScalesTab() {
  const { t } = useTranslation();
  const { canWrite, isHr } = useAuth();
  // Analitik Plan 2 (spec §2.6, §9/2) — seviye bandı: kilitsiz ölçekte formla birlikte kaydedilir;
  // KİLİTLİ ölçekte yalnız bu alan açıktır ve İK'nın seçimi anında kaydedilir (backend: yalnız İK).
  const bandOptions = useMemo(() => [
    { id: '', name: t('performance.ratingScale.bandDefault') },
    { id: 'high', name: t('performance.period.bands.high') },
    { id: 'mid', name: t('performance.period.bands.mid') },
    { id: 'low', name: t('performance.period.bands.low') },
  ], [t]);
  const canManage = canWrite('PM');

  const {
    data, fetching, createScale, updateScale, deleteScale, copyScale, setLevelBand,
  } = useRatingScalesApi();

  const [drawer, setDrawer] = useState({ open: false, row: null });
  const [nameDraft, setNameDraft] = useState('');
  const [levels, setLevels] = useState([]);
  const [saveError, setSaveError] = useState('');
  const [copying, setCopying] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [busy, setBusy] = useState(false);

  const locked = !!drawer.row?.locked;

  useEffect(() => {
    if (drawer.open) {
      setNameDraft(drawer.row?.name || '');
      setLevels(drawer.row ? levelsFromRow(drawer.row) : emptyLevels());
      setSaveError('');
    }
  }, [drawer.open, drawer.row]);

  const openNew = useCallback(() => setDrawer({ open: true, row: null }), []);
  const openEdit = useCallback((row) => setDrawer({ open: true, row }), []);
  const closeDrawer = useCallback(() => setDrawer({ open: false, row: null }), []);

  const addLevel = useCallback(() => {
    setLevels((prev) => {
      if (prev.length >= MAX_LEVELS) return prev;
      const lastValue = prev.length > 0 ? Number(prev[prev.length - 1].numericValue) || 0 : 0;
      return [...prev, {
        _key: `new-${Date.now()}-${prev.length}`, label: '', numericValue: lastValue + 1, triggersPip: false,
      }];
    });
  }, []);

  const removeLevel = useCallback((idx) => {
    setLevels((prev) => (prev.length <= MIN_LEVELS ? prev : prev.filter((_, i) => i !== idx)));
  }, []);

  const moveLevel = useCallback((idx, dir) => {
    setLevels((prev) => {
      const otherIdx = idx + dir;
      if (otherIdx < 0 || otherIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[otherIdx]] = [next[otherIdx], next[idx]];
      return next;
    });
  }, []);

  const updateLevel = useCallback((idx, patch) => {
    setLevels((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }, []);

  // FE erken uyarı: BE zaten 2-10 satır / boş etiket / kesin-artan sayısal
  // değer sırasını reddeder (levelCount|labelRequired|numericOrder) ama
  // ekle/sil butonları sınırı zaten dayattığından bunlar çoğunlukla güvenlik
  // ağı — yalnız mevcut bir skalanın kayıtlı verisi bir şekilde sınır dışıysa
  // (teorik) devreye girer. Başarısızlıkta `false` dönüp drawer'ı AÇIK tutmak
  // GenericFormDrawer'ın onSave sözleşmesiyle birebir (bkz. handleSubmit).
  //
  // İnceleme fix'i (round 1): kilitli skalada alanlar salt-okunur olsa da
  // Footer'ın Save butonu GenericFormDrawer.js'e dokunmadığımız için hâlâ
  // tıklanabilir — guard olmadan bu canlı bir PUT atar, BE 409 `locked`
  // ile reddeder ve kullanıcı "salt-okunur" sunulan ekranda bağlamsız bir
  // hata toast'ı görür. Kilitli satırda update yoluna HİÇ girmeden `false`
  // dönmek (drawer açık kalır, hiçbir ağ isteği çıkmaz) tek doğru davranış
  // — asıl mutasyon yolu zaten "Kopyala" (handleCopy).
  const handleSave = useCallback(async () => {
    if (drawer.row?.locked) return false;

    const trimmedName = nameDraft.trim();
    if (!trimmedName) {
      setSaveError(t('performance.ratingScale.nameRequiredHint'));
      return false;
    }
    if (levels.length < MIN_LEVELS || levels.length > MAX_LEVELS) {
      setSaveError(t('performance.ratingScale.levelCountHint'));
      return false;
    }
    if (levels.some((l) => !l.label.trim())) {
      setSaveError(t('performance.ratingScale.labelRequiredHint'));
      return false;
    }
    for (let i = 1; i < levels.length; i += 1) {
      if (Number(levels[i].numericValue) <= Number(levels[i - 1].numericValue)) {
        setSaveError(t('performance.ratingScale.numericOrderHint'));
        return false;
      }
    }
    setSaveError('');
    const payload = {
      name: trimmedName,
      levels: levels.map((l) => ({
        label: l.label.trim(),
        numericValue: Number(l.numericValue) || 0,
        triggersPip: !!l.triggersPip,
        band: l.band || undefined,
      })),
    };
    return drawer.row
      ? await updateScale(drawer.row.id, payload)
      : await createScale(payload);
  }, [nameDraft, levels, drawer.row, updateScale, createScale, t]);

  const handleCopy = useCallback(async () => {
    if (!drawer.row) return;
    setCopying(true);
    const ok = await copyScale(drawer.row.id);
    setCopying(false);
    if (ok) closeDrawer();
  }, [drawer.row, copyScale, closeDrawer]);

  const confirmDelete = useCallback(async () => {
    setBusy(true);
    await deleteScale(deleteConfirm.id);
    setBusy(false);
    setDeleteConfirm({ isOpen: false, id: null, name: '' });
  }, [deleteScale, deleteConfirm.id]);

  // Kilitli skalada Sil gizlenir (BE zaten 409 `locked` ile reddeder — bkz.
  // RatingScaleDataAccess.Delete); Düzenle her zaman görünür, kilitliyken
  // drawer'ı salt-okunur+Kopyala modunda açar (FeedbackFormsTab'ın
  // rename/delete `condition: (row) => !row.locked` deseninin aynısı).
  const actionOptions = useMemo(() => {
    if (!canManage) return [];
    return [
      { type: 'edit', label: t('common.edit'), onClick: openEdit },
      { type: 'divider' },
      {
        type: 'delete',
        label: t('common.delete'),
        onClick: (row) => setDeleteConfirm({ isOpen: true, id: row.id, name: row.name }),
        condition: (row) => !row.locked,
      },
    ];
  }, [canManage, t, openEdit]);

  const columns = useMemo(() => {
    const base = [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'name',
        label: t('performance.ratingScale.name'),
        options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
      },
      {
        name: 'locked',
        label: t('performance.ratingScale.lockedColumn'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (v) => (
            <span style={{ color: v ? '#B02A37' : '#1B7A3E', fontWeight: 600 }}>
              {v ? t('performance.ratingScale.lockedYes') : t('performance.ratingScale.lockedNo')}
            </span>
          ),
        },
      },
      {
        name: 'levels',
        label: t('performance.ratingScale.levelCountColumn'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (v) => (Array.isArray(v) ? v.length : 0),
        },
      },
    ];
    if (actionOptions.length === 0) return base;
    return [
      ...base,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [t, actionOptions, data]);

  const drawerTitle = useMemo(() => {
    if (!drawer.row) return t('performance.ratingScale.new');
    return locked ? t('performance.ratingScale.view') : t('performance.ratingScale.edit');
  }, [drawer.row, locked, t]);

  return (
    <div>
      <Hint>{t('performance.ratingScale.hint')}</Hint>

      <GenericFormDrawer
        open={drawer.open}
        onClose={closeDrawer}
        title={drawerTitle}
        schema={[]}
        onSave={handleSave}
      >
        {locked && (
          <LockedBadge><Lock size={12} /> {t('performance.ratingScale.lockedBadge')}</LockedBadge>
        )}

        <TextInput
          label={t('performance.ratingScale.name')}
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          disabled={locked}
          required
        />

        <LevelsSection>
          <LevelsSectionTitle>{t('performance.ratingScale.levelsLabel')}</LevelsSectionTitle>
          <LevelCountHint>{t('performance.ratingScale.levelCountHint')}</LevelCountHint>

          {levels.map((lvl, idx) => (
            <LevelCard key={lvl._key}>
              <LevelCardHeader>
                <LevelIndex>{t('performance.ratingScale.levelIndex', { index: idx + 1 })}</LevelIndex>
                {!locked && (
                  <LevelControls>
                    <IconBtn
                      type="button"
                      title={t('performance.ratingScale.moveUp')}
                      disabled={idx === 0}
                      onClick={() => moveLevel(idx, -1)}
                    >
                      <ArrowUp size={13} />
                    </IconBtn>
                    <IconBtn
                      type="button"
                      title={t('performance.ratingScale.moveDown')}
                      disabled={idx === levels.length - 1}
                      onClick={() => moveLevel(idx, 1)}
                    >
                      <ArrowDown size={13} />
                    </IconBtn>
                    <RemoveIconBtn
                      type="button"
                      title={t('common.delete')}
                      disabled={levels.length <= MIN_LEVELS}
                      onClick={() => removeLevel(idx)}
                    >
                      <Trash2 size={13} />
                    </RemoveIconBtn>
                  </LevelControls>
                )}
              </LevelCardHeader>

              <LevelFieldsRow>
                <TextInput
                  label={t('performance.ratingScale.labelField')}
                  value={lvl.label}
                  onChange={(e) => updateLevel(idx, { label: e.target.value })}
                  disabled={locked}
                  required
                />
                <TextInput
                  label={t('performance.ratingScale.numericValueField')}
                  type="number"
                  step="any"
                  value={lvl.numericValue}
                  onChange={(e) => updateLevel(idx, { numericValue: e.target.value })}
                  disabled={locked}
                />
              </LevelFieldsRow>

              <CheckboxInput
                label={t('performance.ratingScale.triggersPipField')}
                checked={!!lvl.triggersPip}
                onChange={locked ? undefined : (val) => updateLevel(idx, { triggersPip: val })}
              />
              <GenericSelect
                label={t('performance.ratingScale.bandField')}
                value={lvl.band || ''}
                // Kilitli ölçekte "varsayılan"a dönüş yok — uç yalnız high/mid/low kabul eder.
                options={locked ? bandOptions.slice(1) : bandOptions}
                idKey="id"
                labelKey="name"
                disabled={locked && !(isHr && lvl.id)}
                onChange={async (val) => {
                  const band = val || null;
                  if (locked) {
                    if (band && await setLevelBand(drawer.row.id, lvl.id, band)) updateLevel(idx, { band });
                    return;
                  }
                  updateLevel(idx, { band });
                }}
              />
            </LevelCard>
          ))}

          {saveError && <SaveErrorText>{saveError}</SaveErrorText>}

          {!locked && (
            <Button type="button" variant="outline" onClick={addLevel} disabled={levels.length >= MAX_LEVELS}>
              + {t('performance.ratingScale.addLevel')}
            </Button>
          )}

          {locked && (
            <>
              <LockedHint>{t('performance.ratingScale.lockedHint')}</LockedHint>
              <Button type="button" variant="outline" onClick={handleCopy} disabled={copying} loading={copying}>
                {t('common.copy')}
              </Button>
            </>
          )}
        </LevelsSection>
      </GenericFormDrawer>

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('performance.common.deleteTitle')}
        message={t('performance.common.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
        loading={busy}
      />

      <DataTable
        title={t('performance.ratingScale.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        serverSide={false}
        canCreateNewRecord={canManage}
        onNewButtonClick={openNew}
        onRowClick={canManage ? openEdit : undefined}
      />
    </div>
  );
}
